/**
 * 极简紫韵模版 - 页脚
 * 
 * 设计理念：深紫渐变背景 + 非对称两栏布局 + 圆角装饰
 * 与sunset（3栏+品牌横幅）、green（共享Footer）都不同：
 * - 深紫到暗黑渐变背景
 * - 上方装饰性曲线分隔
 * - 左右非对称布局（左大右小）
 * - 底部社交图标 + Copyright
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getNavMenus, getFooterMenus } from '@/utils/api';
import { useSite } from '@/context/SiteContext';
import { useSiteConfig } from '@/context/SiteConfigContext';

export default function PurpleFooter() {
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();
  const [navMenus, setNavMenus] = useState<any[]>([]);
  const [footerMenus, setFooterMenus] = useState<any[]>([]);

  const siteName = siteConfig?.site_name || siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const icp = siteConfig?.icp || '';
  const enableVip = siteConfig?.enable_vip !== false;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getNavMenus().then(setNavMenus).catch(() => {}),
      getFooterMenus().then(setFooterMenus).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  const quickLinks = navMenus.slice(0, 4);
  const defaultServiceLinks = [
    { id: '1', name: '发布信息', url: '/publish' },
    { id: '2', name: '会员中心', url: '/dashboard' },
    ...(enableVip ? [{ id: '3', name: 'VIP特权', url: '/vip' }] : []),
    { id: '4', name: '关于我们', url: '/page/about' },
  ];
  const serviceLinks = footerMenus.length > 0
    ? footerMenus.filter((link: any) => enableVip || !link.url?.includes('/vip'))
    : defaultServiceLinks;

  return (
    <footer className="relative bg-gradient-to-br from-[#1a0a2e] via-[#1e1040] to-[#0f0820] text-gray-300">
      {/* 顶部分隔曲线 */}
      <div className="absolute top-0 inset-x-0 h-12 overflow-hidden -translate-y-full">
        <svg viewBox="0 0 1440 48" className="w-full h-full" preserveAspectRatio="none">
          <path
            d="M0 48c240-24 480-36 720-24s480 20 720 12v12H0z"
            className="fill-[#1a0a2e]"
          />
        </svg>
      </div>

      {/* 装饰光晕 */}
      <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-purple-500/5 blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-violet-500/5 blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-10">
        {/* 品牌区 + 非对称网格 */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-12">
          {/* 左大：品牌介绍 (占2列) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              {logo ? (
                <img src={logo} alt={siteName} className="h-9" />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-purple-500/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'P'}
                </div>
              )}
              {siteName && <span className="text-xl font-bold text-white">{siteName}</span>}
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              致力于为用户提供高效、可靠的分类信息服务。让每一条信息都能精准触达目标用户。
            </p>
          </div>

          {/* 右小 (占1列)：快捷链接 */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4">快捷导航</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-gray-400 hover:text-purple-300 transition-colors">首页</Link></li>
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <Link to={link.url || '/'} className="text-sm text-gray-400 hover:text-purple-300 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* (占1列)：服务 */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4">服务</h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.url || '/'} className="text-sm text-gray-400 hover:text-purple-300 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* (占1列)：联系方式 */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4">联系我们</h4>
            <ul className="space-y-2.5">
              {siteConfig?.contact_info?.phone && (
                <li className="flex items-center gap-2 text-sm text-gray-400">
                  <i className="ri-phone-line text-purple-400" />
                  <a href={`tel:${siteConfig.contact_info.phone}`} className="hover:text-purple-300 transition-colors">{siteConfig.contact_info.phone}</a>
                </li>
              )}
              {siteConfig?.contact_info?.email && (
                <li className="flex items-center gap-2 text-sm text-gray-400">
                  <i className="ri-mail-line text-purple-400" />
                  <a href={`mailto:${siteConfig.contact_info.email}`} className="hover:text-purple-300 transition-colors">{siteConfig.contact_info.email}</a>
                </li>
              )}
              {siteConfig?.contact_info?.address && (
                <li className="flex items-center gap-2 text-sm text-gray-400">
                  <i className="ri-map-pin-line text-purple-400" /> {siteConfig.contact_info.address}
                </li>
              )}
              {!siteConfig?.contact_info?.phone && !siteConfig?.contact_info?.email && !siteConfig?.contact_info?.address && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>
        </div>

        {/* 底部分隔线 + Copyright */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} {siteName || '分类信息平台'}. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/page/privacy" className="hover:text-gray-300 transition-colors">隐私政策</Link>
              <Link to="/page/register-agreement" className="hover:text-gray-300 transition-colors">服务协议</Link>
              {icp && <span>{icp}</span>}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
